import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Flame, ShoppingCart, User, Search, Menu, X, LogOut, LayoutDashboard, ChevronDown, LayoutGrid, BookOpen, Sun, Moon } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { SHOP_MENU } from "@/constants/shop";
import useFacets from "@/hooks/useFacets";

// Temos jungiklis: klasė 'dark' ant <html>, pasirinkimas saugomas localStorage
function useTheme() {
  const [dark, setDark] = useState(() => {
    try { return document.documentElement.classList.contains("dark"); } catch { return false; }
  });
  const toggle = () => {
    const next = !dark;
    setDark(next);
    try {
      document.documentElement.classList.toggle("dark", next);
      localStorage.setItem("theme", next ? "dark" : "light");
    } catch {}
  };
  return { dark, toggle };
}

const INFO_NAV = [
  { label: "Naudinga info", to: "/info" },
  { label: "Pristatymas", to: "/pristatymas" },
  { label: "D.U.K.", to: "/duk" },
  { label: "Apie", to: "/mus" },
];

export default function Header() {
  const { brands, services } = useFacets();
  // Meniu: statinės medžiagų grupės + paslaugos + dinaminė brandų grupė iš katalogo
  const menuGroups = [
    ...SHOP_MENU,
    ...(services?.length > 0
      ? [{
          title: "Paslaugos",
          items: [
            { label: "Visos paslaugos", to: "/parduotuve?category=paslaugos" },
            ...services.map((s) => ({ label: s.name, to: `/produktas/${s.slug}` })),
          ],
        }]
      : []),
    ...(brands.length > 0
      ? [{ title: "Prekės ženklas", items: brands.map((b) => ({ label: b, to: `/parduotuve?brand=${encodeURIComponent(b)}` })) }]
      : []),
  ];
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [q, setQ] = useState("");
  const closeTimer = useRef(null);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/parduotuve?q=${encodeURIComponent(q)}`);
      setSearchOpen(false);
      setMobileOpen(false);
    }
  };

  const openShop = () => { clearTimeout(closeTimer.current); setShopOpen(true); };
  const closeShop = () => { closeTimer.current = setTimeout(() => setShopOpen(false), 120); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setShopOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="sticky top-0 z-50 lg:pt-3 lg:px-3" data-testid="site-header">
      {/* Pagrindinė juosta: mobile - pilna juosta, desktop - plaukiantis glass bar */}
      <div className="max-w-screen-2xl mx-auto px-6 h-[68px] flex items-center gap-3 lg:gap-5 bg-white dark:bg-[#111A28] border-b border-gray-200 dark:border-gray-700 lg:border lg:rounded-2xl lg:border-gray-200/70 dark:lg:border-white/10 lg:bg-white/80 dark:lg:bg-[#111A28]/70 lg:backdrop-blur-xl lg:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.55)]">
        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <img src="/logo-3dzona.png" alt="3dzona.lt" className="h-9 w-auto dark:hidden" /><img src="/logo-3dzona-dark.png" alt="3dzona.lt" className="h-9 w-auto hidden dark:block" />
        </Link>

        {/* Parduotuvė mygtukas + kategorijų meniu (desktop) */}
        <div className="hidden lg:block relative shrink-0" onMouseEnter={openShop} onMouseLeave={closeShop}>
          <button
            onClick={() => { if (shopOpen) { navigate("/parduotuve"); setShopOpen(false); } else { setShopOpen(true); } }}
            className="inline-flex items-center gap-2 bg-[#00B5E2] hover:bg-[#0098c0] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            data-testid="shop-menu-btn" aria-expanded={shopOpen} aria-haspopup="true"
          >
            <LayoutGrid size={16} /> Parduotuvė <ChevronDown size={14} className={`transition-transform ${shopOpen ? "rotate-180" : ""}`} />
          </button>

          {shopOpen && (
            <div className="absolute left-0 top-full pt-2 w-[640px] z-50" data-testid="mega-menu">
              <div className="bg-white dark:bg-[#111A28] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-5">
                <div className="grid grid-cols-3 gap-5">
                  {menuGroups.map((group) => (
                    <div key={group.title}>
                      <div className="font-mono text-[11px] uppercase tracking-widest text-[#00B5E2] font-medium mb-2.5">{group.title}</div>
                      <div className="flex flex-col gap-1.5">
                        {group.items.map((it) => (
                          <Link key={it.label} to={it.to} onClick={() => setShopOpen(false)} className="text-sm text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors">{it.label}</Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/parduotuve" onClick={() => setShopOpen(false)} className="mt-4 flex items-center gap-2 bg-[#E6F7FC] dark:bg-[#0A2A38] text-[#0c647c] text-xs px-3.5 py-2.5 rounded-lg hover:bg-[#d4f0f9] dark:hover:bg-[#0E3648] transition-colors">
                  <LayoutGrid size={14} /> Visi produktai parduotuvėje →
                </Link>
              </div>
            </div>
          )}
        </div>

        <Link to="/info" className="hidden lg:inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] shrink-0 transition-colors" data-testid="kb-link">
          <BookOpen size={16} /> Žinynas
        </Link>

        <Link to="/parduotuve?akcija=1" className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF0033] hover:opacity-80 shrink-0 transition-opacity" data-testid="sale-link">
          <Flame size={16} /> Akcijos
        </Link>

        {/* Paieška (desktop matomas laukas) */}
        <form onSubmit={submitSearch} className="hidden lg:flex flex-1 min-w-0" data-testid="search-bar-desktop">
          <div className="flex items-center gap-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3.5 focus-within:border-[#00B5E2] transition-colors">
            <Search size={16} className="text-gray-400 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ieškoti filamento, spalvos, markės..." className="flex-1 min-w-0 py-2.5 text-sm outline-none bg-transparent" data-testid="search-input" />
          </div>
        </form>

        <div className="flex-1 lg:hidden" />

        {/* Dešinės ikonos */}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setSearchOpen((s) => !s)} className="lg:hidden p-2 text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors" data-testid="search-toggle"><Search size={18} /></button>

          <button onClick={toggleTheme} className="p-2 text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors" title={dark ? "Šviesus režimas" : "Tamsus režimas"} data-testid="theme-toggle">
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-1">
              {user.role === "admin" && (
                <Link to="/admin" className="p-2 text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors" title="Admin" data-testid="admin-link"><LayoutDashboard size={18} /></Link>
              )}
              <Link to="/paskyra" className="p-2 text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors" data-testid="account-link"><User size={18} /></Link>
              <button onClick={logout} className="p-2 text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors" data-testid="logout-btn"><LogOut size={18} /></button>
            </div>
          ) : (
            <Link to="/prisijungti" className="hidden md:flex p-2 text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors" data-testid="login-link"><User size={18} /></Link>
          )}

          <Link to="/krepselis" className="relative p-2 text-gray-700 dark:text-gray-300 hover:text-[#00B5E2] transition-colors" data-testid="cart-link">
            <ShoppingCart size={18} />
            {count > 0 && (
              <span className="absolute top-0 right-0 bg-[#00B5E2] text-white text-[10px] font-mono w-4 h-4 flex items-center justify-center rounded-full pop" data-testid="cart-count">{count}</span>
            )}
          </Link>

          <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobili paieška */}
      {searchOpen && (
        <form onSubmit={submitSearch} className="lg:hidden border-t border-gray-200 dark:border-gray-700 bg-[#F8F9FA] dark:bg-[#0B1220]" data-testid="search-bar">
          <div className="max-w-screen-2xl mx-auto px-6 py-4 flex gap-3">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ieškoti filamento, spalvos, prekės ženklo..." className="input flex-1" data-testid="search-input-mobile" />
            <button className="btn-primary" data-testid="search-submit">Ieškoti</button>
          </div>
        </form>
      )}

      {/* Mobilus meniu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-[#111A28] p-4 space-y-4" data-testid="mobile-menu">
          <Link to="/parduotuve" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 bg-[#00B5E2] text-white px-4 py-2.5 rounded-lg text-sm font-medium">
            <LayoutGrid size={16} /> Visa parduotuvė
          </Link>
          <Link to="/info" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 border border-[#00B5E2] text-[#00B5E2] px-4 py-2.5 rounded-lg text-sm font-medium">
            <BookOpen size={16} /> Žinynas
          </Link>
          {menuGroups.map((group) => (
            <div key={group.title}>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#00B5E2] font-medium mb-2">{group.title}</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {group.items.map((it) => <Link key={it.label} to={it.to} onClick={() => setMobileOpen(false)} className="text-sm text-gray-700 dark:text-gray-300 py-1">{it.label}</Link>)}
              </div>
            </div>
          ))}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-1">
            {INFO_NAV.map((n) => <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="block py-2 text-gray-700 dark:text-gray-300">{n.label}</Link>)}
            <Link to="/privatumas" onClick={() => setMobileOpen(false)} className="block py-2 text-gray-700 dark:text-gray-300">Privatumo politika</Link>
            {!user && <Link to="/prisijungti" onClick={() => setMobileOpen(false)} className="block py-2 text-[#00B5E2]">Prisijungti</Link>}
            {user && <Link to="/paskyra" onClick={() => setMobileOpen(false)} className="block py-2 text-gray-700 dark:text-gray-300">Mano paskyra</Link>}
            {user?.role === "admin" && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2 text-[#00B5E2]">Admin panelė</Link>}
            {user && <button onClick={() => { logout(); setMobileOpen(false); }} className="block py-2 text-gray-700 dark:text-gray-300 text-left w-full">Atsijungti</button>}
          </div>
        </div>
      )}
    </header>
  );
}
